import { gql, NetworkStatus, useQuery } from "@apollo/client";
import { shiftStreamClient } from "../../../graphql/client";

export const PAGE_SIZE = 50;

export const GET_STREAMS = gql`
  query getStreams(
    $ids: [Int!]
    $accountId: Int
    $sourceAccountId: Int
    $roleId: Int
    $venueId: Int
    $state: StreamState
    $includeFilled: Boolean
    $startDate: DateTimeISO
    $endDate: DateTimeISO
    $orderBy: StreamOrder
    $offset: Int!
    $limit: Int!
  ) {
    streams(
      ids: $ids
      accountId: $accountId
      sourceAccountId: $sourceAccountId
      roleId: $roleId
      venueId: $venueId
      includeFilled: $includeFilled
      startDate: $startDate
      endDate: $endDate
      orderBy: $orderBy
      state: $state
      offset: $offset
      limit: $limit
    ) {
      total

      data {
        id
        accountId
        state
        shiftId
        adoptedShiftIds
        key
        noAlgo
        slots

        account {
          id
          provider
          name
          url
          login
        }

        shift {
          id
          startTime
          endTime
          sourceAccount {
            id
            accountName
          }
          role {
            id
            name
          }
          venue {
            id
            name
          }
          subvenue {
            id
            name
          }
        }
      }
    }
  }
`;

export type StreamOrder = "ID" | "CLIENT" | "VENUE" | "ROLE" | "START";

export const useStreams = (
  {
    ids,
    accountId,
    sourceAccountId,
    roleId,
    venueId,
    state,
    startDate,
    endDate,
    includeFilled,
    orderBy,
  }: {
    ids: number[] | null;
    accountId: number | null;
    sourceAccountId: number | null;
    roleId: number | null;
    venueId: number | null;
    state: string;
    includeFilled: boolean;
    startDate: moment.Moment | null;
    endDate: moment.Moment | null;
    orderBy: "ID" | "CLIENT" | "VENUE" | "ROLE" | "START";
  },
  page = 1,
) => {
  const safePage = Math.max(1, page);
  const offset = (safePage - 1) * PAGE_SIZE;

  const { data, previousData, loading, networkStatus, refetch } = useQuery(
    GET_STREAMS,
    {
      client: shiftStreamClient,
      variables: {
        ids,
        state: ids && ids.length > 0 ? undefined : state,
        accountId,
        sourceAccountId,
        roleId,
        venueId,
        includeFilled,
        startDate,
        endDate,
        orderBy,
        offset,
        limit: PAGE_SIZE,
      },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
      onError: (error) => {
        throw error;
      },
    },
  );

  // Keep pagination metadata stable while the next page is loading.
  const resolvedData = data || previousData;

  const total = resolvedData?.streams?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return {
    loading: loading || networkStatus === NetworkStatus.refetch,
    streams: resolvedData?.streams.data || [],
    total,
    totalPages,
    page: safePage,
    refetch,
  };
};
